"use client";
import { jsx, jsxs } from "react/jsx-runtime";
import { useReducer, useEffect } from "react";
import { Box } from "@mui/material";
import { Text } from "@nxt-costco-com/forge-components";
import GenericImageHandler from "../../Util/GenericImageHandler.js";
import { ConditionalLink } from "./ConditionalLink/index.js";
import { COMPONENT_NAME } from "./constants.js";
import { ActionType } from "./types.js";
import { containerStyles, overLayStyles, overlayTextStyles } from "./styles.js";
const initialState = {
  isHovered: false
};
function reducer(state, action) {
  switch (action.type) {
    case ActionType.setIsHovered:
      return { ...state, isHovered: action.payload };
    case ActionType.setEffectiveImgSrc:
      return { ...state, effectiveImgSrc: action.payload };
    case ActionType.setEffectiveAltText:
      return { ...state, effectiveAltText: action.payload };
    default:
      return state;
  }
}
const ProductImage = ({
  src,
  srcFallback,
  title,
  titleFallback,
  //  A11y requirement when it's used in drawer
  hideImageFromAccessibility = false,
  href,
  overlayText,
  sx = {},
  uniqueId = "",
  ariaHidden = false
}) => {
  const { maxHeight } = sx;
  const [{ isHovered, effectiveImgSrc, effectiveAltText }, dispatch] = useReducer(reducer, {
    ...initialState,
    effectiveAltText: title,
    effectiveImgSrc: src
  });
  useEffect(() => {
    dispatch({
      payload: src,
      type: ActionType.setEffectiveImgSrc
    });
  }, [src]);
  if (!src && !srcFallback) {
    console.error(
      `The ${COMPONENT_NAME} component requires a src or srcFallback prop, but none was provided.`
    );
    return null;
  }
  const onImageError = () => {
    if (effectiveImgSrc !== srcFallback && srcFallback) {
      dispatch({
        payload: srcFallback,
        type: ActionType.setEffectiveImgSrc
      });
      dispatch({
        payload: titleFallback,
        type: ActionType.setEffectiveAltText
      });
    }
  };
  const imageContainerStyles = { ...containerStyles, ...sx };
  const finalImageAlt = hideImageFromAccessibility ? "" : effectiveAltText || titleFallback;
  return /* @__PURE__ */ jsx(
    Box,
    {
      onMouseEnter: () => dispatch({
        payload: true,
        type: ActionType.setIsHovered
      }),
      onMouseOut: () => dispatch({
        payload: false,
        type: ActionType.setIsHovered
      }),
      sx: [
        ...Array.isArray(imageContainerStyles) ? imageContainerStyles : [imageContainerStyles]
      ],
      "aria-hidden": ariaHidden,
      "data-testid": `${COMPONENT_NAME}_${uniqueId}`,
      children: /* @__PURE__ */ jsxs(ConditionalLink, { href, uniqueId, children: [
        overlayText && !isHovered && /* @__PURE__ */ jsx(Box, { sx: { ...overLayStyles }, children: /* @__PURE__ */ jsx(
          Text,
          {
            sx: overlayTextStyles,
            uniqueId,
            variant: "t2",
            children: overlayText
          }
        ) }),
        /* @__PURE__ */ jsx(
          GenericImageHandler,
          {
            sx: {
              maxHeight,
              maxWidth: "100%"
            },
            alt: finalImageAlt,
            loading: "lazy",
            onError: onImageError,
            src: effectiveImgSrc || srcFallback,
            widths: [{ width: 320 }],
            hidImageFromAccessibility: hideImageFromAccessibility
          }
        )
      ] })
    }
  );
};
ProductImage.displayName = COMPONENT_NAME;
export {
  ProductImage
};
